import React from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { Row, Col } from 'react-bootstrap';
import { findIndex as _findIndex, isObject as _isObject } from 'lodash';

import { formStatusDispatchHelper } from 'actions/FormStatusActions';
import { addNotification } from 'actions/NotificationsActions';
import { getElementTypes } from 'helpers/cms';
import { getThumbFileFromEntity } from 'helpers/items';

import EntityPropsUpdater from 'component/EntityPropsUpdater';
import Entity from 'components/Entity';
import IntlFormInlineSelector from 'components/intlForm/IntlFormInlineSelector';

import CmsCanvas from 'components/cms/CmsCanvas';

import FormInput from 'components/forms/FormInput';
import FormSwitchery from 'components/forms/FormSwitchery';
import FormAutocomplete from 'components/forms/FormAutocomplete';
import FormImageWithPreview from 'components/forms/FormImageWithPreview';

import { entityMetaMapping } from 'js/constants';

const defaultCmsContent = [
  {
    type: 'grid',
    params: [{ name: 'isFluid', value: 'yes' }],
  },
];

class CmsElementData extends EntityPropsUpdater {
  componentDidUpdate = prevProps => {
    const {
      intl: { messages },
      entity,
      formStatusImage,
      doNotify,
      changeEntity,
    } = this.props;

    if (prevProps.formStatusImage.pending && !formStatusImage.pending && !formStatusImage.hasError) {
      // image has been uploaded, update the entity
      const value = entity.createValue('preview', [], formStatusImage.response);
      const newEntity = { ...entity.updateValue('preview', value) };
      changeEntity(newEntity);

      doNotify(1, messages.file_uploaded);
    }
  };

  // make sure there is a CMS description for given language
  getCmsContentEntity = () => {
    const { entity, appIntl } = this.props;

    const contentIndex = _findIndex(entity.getContent(), ['language', appIntl.displayIntlForm]);
    if (contentIndex > -1) {
      return this.getEntityForCms(entity.getContent()[contentIndex]);
    }

    // create new value and return it
    const newValue = entity.getContent().slice();
    const newContent = entity.createValue('content', [], {
      language: appIntl.displayIntlForm,
      content: defaultCmsContent,
    });
    newValue.push(newContent);

    this.updateEntity('content', newValue);

    return this.getEntityForCms(newContent);
  };

  getEntityForCms = data => {
    const entity = new Entity();
    entity.setData(data);
    entity.setMeta(this.props.meta, entityMetaMapping.cmsDescription);

    return entity;
  };

  updateCmsContent = content => {
    const { entity, appIntl } = this.props;
    const contentIndex = _findIndex(entity.getContent(), ['language', appIntl.displayIntlForm]);
    this.updateEntity('content', content, ['content', contentIndex.toString()]);
  };

  fileUpload = (name, images) => {
    const { entity, uploadPreview } = this.props;

    if (_isObject(images) && images.length === 1) {
      const data = new FormData();
      data.append('image', images[0]);

      uploadPreview(entity.getId(), data);
    }
  };

  render() {
    const {
      intl: { messages },
      entity,
      entityLoaded,
      disabled,
      inModal,
    } = this.props;

    let imageUrl;
    if (entityLoaded) {
      if (entity.getPreview().getId()) {
        const thumbnail = getThumbFileFromEntity(entity.getPreview());
        if (thumbnail) {
          imageUrl = thumbnail.getWebLink();
        }
      }
    }

    return (
      <div className="clearfix">
        {entityLoaded && (
          <>
            <Row>
              <Col xs={12} md={inModal ? 12 : 6}>
                <div className="panel panel-transparent">
                  <div className="panel-body">
                    <p className="form-group-title">{messages.cms_element}</p>
                    <div className="form-group-attached">
                      <FormInput
                        label={messages.name}
                        name="name"
                        value={entity.getName()}
                        meta={entity.getNameMetaInformation()}
                        validation={entity.getNameValidationResult()}
                        onChange={this.updateEntity}
                        disabled={disabled}
                      />
                      <div class="form-group form-group-default">
                        <FormSwitchery
                          name="isReference"
                          label={messages.use_this_element_as_reference}
                          value
                          checked={entity.getIsReference()}
                          onChange={this.updateEntity}
                          disabled={disabled}
                          infoText={messages.you_can_reuse_this_element_on_multiple_pages}
                        />
                      </div>
                      {!entity.getIsReference() && (
                        <>
                          <FormAutocomplete
                            label={messages.type}
                            name="type"
                            value={entity.getType()}
                            meta={entity.getTypeMetaInformation()}
                            validation={entity.getTypeValidationResult()}
                            onChange={this.updateEntity}
                            disabled={disabled}
                            options={getElementTypes(messages)}
                          />
                          <FormImageWithPreview
                            label={messages.image}
                            name="image"
                            url={imageUrl}
                            onChange={this.fileUpload}
                            onReset={this.fileReset}
                            pending={this.props.formStatusImage.pending}
                          />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            <Row>
              <Col xs={12}>
                <div className="panel panel-transparent">
                  <div className="panel-body">
                    <div className="p-t-15 p-b-15">
                      <IntlFormInlineSelector /> {messages.content}
                    </div>
                    <CmsCanvas
                      editMode
                      entity={this.getCmsContentEntity()}
                      changeEntity={cmsContentEntity => {
                        this.updateCmsContent(cmsContentEntity.processedData.content);
                      }}
                      pageId={entity.getId()}
                      disableNewContainer
                    />
                  </div>
                </div>
              </Col>
            </Row>
          </>
        )}
      </div>
    );
  }
}
CmsElementData.defaultProps = {
  inModal: false,
};

const mapStateToProps = state => {
  return {
    appIntl: state.intl,
    meta: state.entityMeta,
    formStatusImage: state.formStatus.cmsElementPreviewImage,
  };
};
const mapDispatchToProps = dispatch => {
  return {
    doNotify: (code, text) => {
      dispatch(addNotification(code, text));
    },
    uploadPreview: (cmsDynamicElementId, args) => {
      formStatusDispatchHelper(
        'cmsElementPreviewImage',
        `/api/dynamicelements/${cmsDynamicElementId}/preview.json`,
        args,
        'post',
        dispatch
      );
    },
  };
};

export default withRouter(
  injectIntl(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(CmsElementData)
  )
);
